// USGA WHS handicap calculator formulas — ported from the proven
// GolfGamesScorecard app. Object-argument API, kept isolated in this module.
//
// Relationship to src/lib/handicap.js (do NOT merge — reconciled, not duplicated):
//   - handicap.js exports a POSITIONAL calcCourseHandicap(index, slope, rating, par)
//     that the live AI lookup + registration + Team Cup already depend on. It is
//     left exactly as-is.
//   - This module adds the OBJECT-arg WHS set the calculator (and a future scoring
//     wiring) needs, including the UNROUNDED raw course handicap that correct
//     allowance math requires. calcPlayingHandicap / ALLOWANCE_OPTIONS /
//     DEFAULT_ALLOWANCE already exist and match the spec, so they are re-exported
//     from handicap.js rather than redefined.
//
// This module is pure (no React / Firebase) so it runs under `node --test`.

import { calcPlayingHandicap, ALLOWANCE_OPTIONS, DEFAULT_ALLOWANCE } from './handicap.js'

export { calcPlayingHandicap, ALLOWANCE_OPTIONS, DEFAULT_ALLOWANCE }

// USGA standard "alternating front/back" default stroke index — harder holes
// on the front (even SIs front, odd back). Used when a course's own SI isn't set.
export const DEFAULT_STROKE_INDEX = [
  10, 2, 16, 6, 14, 4, 18, 8, 12,   // holes 1–9
   5, 1, 13, 3, 15, 7, 17, 9, 11,   // holes 10–18
]

/**
 * UNROUNDED Course Handicap (USGA WHS):
 *   CH_raw = Index × (Slope ÷ 113) + (Rating − Par)
 *
 * Any invalid input → 0 so nothing downstream ever sees NaN. This raw value is
 * what the Playing-Handicap allowance math must use (see calcPlayingHandicap):
 * pre-rounding the CH before applying the allowance is the GHIN-app bug this
 * port was written to avoid.
 */
export function calcCourseHandicapRaw({ handicapIndex, slopeRating, courseRating, par }) {
  const idx = Number(handicapIndex)
  const slope = Number(slopeRating)
  const rating = Number(courseRating)
  const p = Number(par)
  if (!Number.isFinite(idx) || !Number.isFinite(slope) || slope <= 0
      || !Number.isFinite(rating) || !Number.isFinite(p)) return 0
  return idx * (slope / 113) + (rating - p)
}

/** Rounded Course Handicap — what the player sees. Stroke allocation and net
 *  math must NOT pass through this; use calcCourseHandicapRaw + calcPlayingHandicap. */
export function calcCourseHandicap(input) {
  return Math.round(calcCourseHandicapRaw(input))
}

/**
 * Effective handicap for stroke allocation / net scoring: the Playing Handicap
 * when present, else Course Handicap × allowance, else bare Course Handicap
 * (legacy rounds with neither).
 */
export function effectiveHandicap(p) {
  if (Number.isFinite(p?.playingHandicap)) return p.playingHandicap
  if (Number.isFinite(p?.handicapAllowance)) return calcPlayingHandicap(p.courseHandicap, p.handicapAllowance)
  return p?.courseHandicap
}

/**
 * Strokes a player receives on a single hole given their Course Handicap and
 * the course's stroke index.
 *   CH 20 + hardest holes (SI 1,2) → 2 strokes; every other hole → 1
 *   CH 0 or negative (plus players) → 0 everywhere (floored, no strokes back)
 * No 9-hole path — assumes an 18-hole stroke index.
 */
export function strokesOnHole(courseHandicap, holeIndex, strokeIndex = DEFAULT_STROKE_INDEX) {
  if (!Number.isFinite(courseHandicap) || courseHandicap <= 0) return 0
  if (holeIndex < 0 || holeIndex >= 18) return 0
  const ch = Math.floor(courseHandicap)
  const si = strokeIndex[holeIndex] || DEFAULT_STROKE_INDEX[holeIndex]
  const base = Math.floor(ch / 18)
  const extra = (ch % 18) >= si ? 1 : 0
  return base + extra
}

/**
 * Convert a 2D gross grid → 2D net grid by subtracting each player's per-hole
 * strokes received (via effectiveHandicap). Empty cells (null) stay null.
 */
export function netScoreGrid(grossScores, players, strokeIndex = DEFAULT_STROKE_INDEX) {
  return grossScores.map((row, pi) => {
    const eff = players[pi] ? effectiveHandicap(players[pi]) : 0
    return row.map((s, h) => (s == null ? null : s - strokesOnHole(eff, h, strokeIndex)))
  })
}

/** True when arr is length 18 AND a permutation of 1..18 (each value once). */
export function isValidStrokeIndex(arr) {
  if (!Array.isArray(arr) || arr.length !== 18) return false
  const set = new Set()
  for (const n of arr) {
    if (!Number.isInteger(n) || n < 1 || n > 18) return false
    set.add(n)
  }
  return set.size === 18
}
